import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import SpecialistDashboardLayout from '../../components/SpecialistDashboardLayout';
import { API_URL, SOCKET_URL } from '../../config/api';
import { useCurrency } from '../../context/CurrencyContext';
import './SpecialistProjectsPage.css';

// Helper to resolve image URLs (relative paths → full backend URL)
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const emptyForm = {
  title: '', description: '', projectType: '', budget: '', currency: '',
  startDate: '', endDate: '', country: '', city: '', address: '',
  image: '', companyName: '', contactName: '', contactEmail: '', contactPhone: '', status: 'draft'
};

const SpecialistProjectsPage = () => {
  const navigate = useNavigate();
  const { specialist } = useSpecialistAuth();
  const { selectedCurrency, currencies } = useCurrency();

  // Check if specialist's plan allows project creation
  const planName = (specialist?.membershipPlan || 'basic').toLowerCase();
  const canSubmitProjects = planName.includes('gcc') || planName.includes('business') || planName.includes('global') || planName.includes('enterprise') || planName === 'professional';
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const projectsPerPage = 13;

  const categoryOptions = [
    'Building Materials', 'Infrastructure', 'Residential', 'Commercial',
    'Industrial', 'Hospitality', 'Healthcare', 'Educational',
    'Retail', 'Mixed Use', 'Government', 'Building Construction'
  ];

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsProject, setDetailsProject] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editingProject, setEditingProject] = useState(null); // null = create, object = edit
  const [formData, setFormData] = useState({ ...emptyForm, currency: selectedCurrency.code });
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [wizardStep, setWizardStep] = useState(1);

  // Stock construction images for gallery
  const stockImages = [
    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400',
    'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400',
    'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400',
    'https://images.unsplash.com/photo-1429497419816-9ca5cfb4571a?w=400',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400',
    'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=400',
    'https://images.unsplash.com/photo-1590274853856-f22d5ee3d228?w=400',
    'https://images.unsplash.com/photo-1565008447742-97f6f38c985c?w=400',
    'https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?w=400',
    'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
    'https://images.unsplash.com/photo-1574362848149-11496d93a7c7?w=400',
    'https://images.unsplash.com/photo-1495277319826-a03d6b17ccba?w=400'
  ];

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchProjects();
  }, [currentPage, debouncedSearch, countryFilter, statusFilter, categoryFilter]);

  // Close action menu & category dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      setOpenActionMenu(null);
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', projectsPerPage);
      params.append('offset', (currentPage - 1) * projectsPerPage);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (statusFilter && statusFilter !== 'All') params.append('status', statusFilter.toLowerCase());
      if (countryFilter) params.append('country', countryFilter);
      if (categoryFilter) params.append('projectType', categoryFilter);

      const res = await fetch(`${API_URL}/projects?${params.toString()}`);
      const data = await res.json();
      setProjects(data.projects || []);
      setTotalProjects(data.total || 0);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalProjects / projectsPerPage) || 1;

  const toggleSelectAll = () => {
    if (selectedIds.length === projects.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(projects.map(p => p.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm},${dd},${yyyy}`;
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Format budget (same as admin)
  const formatBudget = (budget, currency) => {
    if (!budget) return 'N/A';
    const cur = currency || selectedCurrency.code;
    if (budget >= 1000000) return `${cur} ${(budget / 1000000).toFixed(1)}M`;
    if (budget >= 1000) return `${cur} ${(budget / 1000).toFixed(0)}K`;
    return `${cur} ${budget}`;
  };

  const handlePromote = async (id) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;
    try {
      const newStatus = project.promoted ? false : true;
      await fetch(`${API_URL}/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promoted: newStatus })
      });
      fetchProjects();
    } catch (err) {
      console.error('Error promoting project:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await fetch(`${API_URL}/projects/${id}`, { method: 'DELETE' });
      fetchProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
    }
  };

  // ========== CREATE / EDIT ==========
  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({ ...emptyForm, currency: selectedCurrency.code });
    setFormError('');
    setWizardStep(1);
    setShowCreateModal(true);
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || '',
      description: project.description || '',
      projectType: project.projectType || '',
      budget: project.budget || '',
      currency: project.currency || selectedCurrency.code,
      startDate: project.startDate ? project.startDate.split('T')[0] : '',
      endDate: project.endDate ? project.endDate.split('T')[0] : '',
      country: project.country || '',
      city: project.city || '',
      address: project.address || '',
      image: project.image || '',
      companyName: project.companyName || '',
      contactName: project.contactName || '',
      contactEmail: project.contactEmail || '',
      contactPhone: project.contactPhone || '',
      status: project.status || 'draft'
    });
    setFormError('');
    setWizardStep(1);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setEditingProject(null);
    setFormData({ ...emptyForm, currency: selectedCurrency.code });
    setFormError('');
    setWizardStep(1);
  };

  const handleFormChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      setFormError('Title and Description are required.');
      return;
    }
    setFormSaving(true);
    setFormError('');
    try {
      const token = localStorage.getItem('specialist_token');
      if (editingProject) {
        // UPDATE
        const res = await fetch(`${API_URL}/projects/${editingProject.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(formData)
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to update'); }
      } else {
        // CREATE
        const res = await fetch(`${API_URL}/projects/specialist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(formData)
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to create'); }
      }
      closeCreateModal();
      fetchProjects();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormSaving(false);
    }
  };

  // ========== DETAILS ==========
  const openDetailsModal = async (projectId) => {
    setDetailsLoading(true);
    setShowDetailsModal(true);
    setDetailsProject(null);
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}`);
      const data = await res.json();
      setDetailsProject(data);
    } catch (err) {
      console.error('Error fetching project details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailsProject(null);
  };

  return (
    <SpecialistDashboardLayout>
      <div className="sp-projects-page">
        {/* Page Header */}
        <div className="sp-projects-header">
          <div className="sp-projects-header-left">
            <h1 className="sp-projects-title">Project Listing</h1>
            <p className="sp-projects-subtitle">Manage your leads, track orders, and grow your business — all in one place.</p>
          </div>
          <button
            className="sp-create-project-btn"
            onClick={() => canSubmitProjects ? navigate('/specialist-projects/create') : navigate('/specialist-membership')}
            title={canSubmitProjects ? 'Create a new project' : 'Upgrade plan to create projects'}
          >
            {canSubmitProjects ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3.33337V12.6667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3.33337 8H12.6667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <span style={{ marginRight: '4px' }}>🔒</span>
            )}
            {canSubmitProjects ? 'Create Project' : 'Upgrade to Create'}
          </button>
        </div>

        {/* Project Lists Section */}
        <div className="sp-projects-section">
          <div className="sp-projects-toolbar">
            <h2 className="sp-projects-section-title">Project Lists</h2>
            <div className="sp-projects-filters">
              <div className="sp-search-box">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
                <svg className="sp-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#999" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 14L11.1 11.1" stroke="#999" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="sp-filter-select-wrapper">
                <select
                  className="sp-filter-select"
                  value={countryFilter}
                  onChange={(e) => { setCountryFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">Country</option>
                  <option value="UAE">UAE</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="Qatar">Qatar</option>
                  <option value="Kuwait">Kuwait</option>
                  <option value="Bahrain">Bahrain</option>
                  <option value="Oman">Oman</option>
                </select>
                <svg className="sp-select-arrow" width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M1 1L5 5L9 1" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="sp-filter-select-wrapper">
                <select
                  className="sp-filter-select"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="published">Published</option>
                  <option value="unpublished">Unpublished</option>
                  <option value="rejected">Rejected</option>
                  <option value="archived">Archived</option>
                  <option value="completed">Completed</option>
                </select>
                <svg className="sp-select-arrow" width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M1 1L5 5L9 1" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="sp-category-filter-wrapper" ref={categoryDropdownRef}>
                <button className="sp-filter-category-btn" onClick={(e) => { e.stopPropagation(); setShowCategoryDropdown(prev => !prev); }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M12.8333 1.16663H1.16667L5.83333 6.69329V10.5L8.16667 11.6666V6.69329L12.8333 1.16663Z" stroke="#1F396C" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {categoryFilter ? categoryFilter : 'Filter By Category'}
                </button>
                {showCategoryDropdown && (
                  <div className="sp-category-dropdown">
                    <div className="sp-category-dropdown-item" onClick={() => { setCategoryFilter(''); setShowCategoryDropdown(false); setCurrentPage(1); }}>
                      All Categories
                    </div>
                    {categoryOptions.map(cat => (
                      <div
                        key={cat}
                        className={`sp-category-dropdown-item ${categoryFilter === cat ? 'active' : ''}`}
                        onClick={() => { setCategoryFilter(cat); setShowCategoryDropdown(false); setCurrentPage(1); }}
                      >
                        {cat}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="sp-projects-table-wrapper">
            <table className="sp-projects-table">
              <thead>
                <tr>
                  <th className="sp-th-checkbox">
                    <label className="sp-checkbox-wrapper">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === projects.length && projects.length > 0}
                        onChange={toggleSelectAll}
                      />
                      <span className="sp-checkbox-custom"></span>
                      <span className="sp-checkbox-label">All</span>
                    </label>
                  </th>
                  <th>Image</th>
                  <th>Creation date</th>
                  <th>Project Title</th>
                  <th>Project Type</th>
                  <th>Location</th>
                  <th>Company</th>
                  <th>Budget</th>
                  <th>Deadline</th>
                  <th>Views</th>
                  <th>Status</th>
                  <th>Details</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="14" className="sp-table-empty">
                      <div className="sp-loading-spinner"></div>
                      <p>Loading projects...</p>
                    </td>
                  </tr>
                ) : projects.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="sp-table-empty">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.3, marginBottom: '8px' }}>
                        <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <p style={{ fontWeight: 500, color: '#666' }}>No projects found</p>
                      <p style={{ fontSize: '13px', color: '#999' }}>Click "Create Project" to get started</p>
                    </td>
                  </tr>
                ) : (
                  projects.map((project) => (
                    <tr key={project.id}>
                      <td className="sp-td-checkbox">
                        <label className="sp-checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(project.id)}
                            onChange={() => toggleSelect(project.id)}
                          />
                          <span className="sp-checkbox-custom"></span>
                        </label>
                      </td>
                      <td className="sp-td-image">
                        <div className="sp-project-image">
                          {project.image ? (
                            <img src={getImageUrl(project.image)} alt={project.title} />
                          ) : (
                            <div className="sp-project-image-placeholder">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#ccc" strokeWidth="1.5"/>
                                <circle cx="8.5" cy="8.5" r="1.5" stroke="#ccc" strokeWidth="1.5"/>
                                <path d="M21 15L16 10L5 21" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="sp-td-date">{formatDate(project.createdAt)}</td>
                      <td className="sp-td-name" title={project.title}>
                        {project.title || 'N/A'}
                      </td>
                      <td className="sp-td-type">{project.projectType || 'N/A'}</td>
                      <td className="sp-td-location">{project.country || project.city || 'N/A'}</td>
                      <td className="sp-td-company">{project.companyName || 'N/A'}</td>
                      <td className="sp-td-budget">{formatBudget(project.budget, project.currency)}</td>
                      <td className="sp-td-deadline">{formatDate(project.endDate)}</td>
                      <td className="sp-td-viewed">{project.views || 0}</td>
                      <td className="sp-td-status">
                        <span className={`sp-status-badge ${(project.status || 'draft')}`}>
                          {({
                            draft: 'Draft',
                            pending_approval: 'Pending',
                            approved: 'Approved',
                            published: 'Published',
                            unpublished: 'Unpublished',
                            rejected: 'Rejected',
                            archived: 'Archived',
                            completed: 'Completed'
                          })[project.status] || project.status || 'Draft'}
                        </span>
                      </td>
                      <td className="sp-td-details">
                        <button className="sp-details-btn" onClick={() => openDetailsModal(project.id)}>Details</button>
                      </td>
                      <td className="sp-td-action">
                        <div className="sp-action-menu-wrapper">
                          <button
                            className="sp-action-dots-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenu(openActionMenu === project.id ? null : project.id);
                            }}
                          >
                            <svg width="4" height="16" viewBox="0 0 4 16" fill="none">
                              <circle cx="2" cy="2" r="1.5" fill="#666"/>
                              <circle cx="2" cy="8" r="1.5" fill="#666"/>
                              <circle cx="2" cy="14" r="1.5" fill="#666"/>
                            </svg>
                          </button>
                          {openActionMenu === project.id && (
                            <div className="sp-action-dropdown">
                              <button onClick={() => { setOpenActionMenu(null); navigate('/specialist-projects/create', { state: { project } }); }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 8, verticalAlign: 'middle' }}>
                                  <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Edit
                              </button>
                              <button onClick={() => { setOpenActionMenu(null); handleDelete(project.id); }} className="sp-action-delete">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 8, verticalAlign: 'middle' }}>
                                  <path d="M3 6H5H21" stroke="#DC3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#DC3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="sp-pagination">
              <button
                className="sp-page-btn sp-page-arrow"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &lt;
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`sp-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                className="sp-page-btn sp-page-arrow sp-page-arrow-next"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ==================== CREATE / EDIT MODAL (3-Step Wizard) ==================== */}
      {showCreateModal && (
        <div className="sp-modal-overlay" onClick={closeCreateModal}>
          <div className="sp-modal sp-modal-wizard" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <h2>{editingProject ? 'Edit Project' : 'Create New Project'}</h2>
              <button className="sp-modal-close" onClick={closeCreateModal}>&times;</button>
            </div>

            {/* Step Indicator */}
            <div className="sp-wizard-steps">
              <div className={`sp-wizard-step ${wizardStep >= 1 ? 'active' : ''} ${wizardStep > 1 ? 'completed' : ''}`}>
                <div className="sp-wizard-step-number">{wizardStep > 1 ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg> : '1'}</div>
                <span>Project Details</span>
              </div>
              <div className="sp-wizard-line"></div>
              <div className={`sp-wizard-step ${wizardStep >= 2 ? 'active' : ''} ${wizardStep > 2 ? 'completed' : ''}`}>
                <div className="sp-wizard-step-number">{wizardStep > 2 ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg> : '2'}</div>
                <span>Description</span>
              </div>
              <div className="sp-wizard-line"></div>
              <div className={`sp-wizard-step ${wizardStep >= 3 ? 'active' : ''}`}>
                <div className="sp-wizard-step-number">3</div>
                <span>Images</span>
              </div>
            </div>

            <div className="sp-modal-body">
              {formError && <div className="sp-form-error">{formError}</div>}

              {/* ===== STEP 1: Project Details ===== */}
              {wizardStep === 1 && (
                <div className="sp-wizard-content">
                  <div className="sp-form-row">
                    <div className="sp-form-group sp-form-full">
                      <label>Project Title <span className="sp-required">*</span></label>
                      <input type="text" name="title" value={formData.title} onChange={handleFormChange} placeholder="Enter project title" />
                    </div>
                  </div>

                  <div className="sp-form-row">
                    <div className="sp-form-group">
                      <label>Category</label>
                      <select name="projectType" value={formData.projectType} onChange={handleFormChange}>
                        <option value="">Select Category</option>
                        <option value="Building Construction">Building Construction</option>
                        <option value="Infrastructure">Infrastructure</option>
                        <option value="Residential">Residential</option>
                        <option value="Commercial">Commercial</option>
                        <option value="Industrial">Industrial</option>
                        <option value="Hospitality">Hospitality</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Educational">Educational</option>
                        <option value="Retail">Retail</option>
                        <option value="Mixed Use">Mixed Use</option>
                        <option value="Government">Government</option>
                      </select>
                    </div>
                    <div className="sp-form-group">
                      <label>Budget</label>
                      <input type="number" name="budget" value={formData.budget} onChange={handleFormChange} placeholder="e.g. 500000" />
                    </div>
                  </div>

                  <div className="sp-form-row">
                    <div className="sp-form-group">
                      <label>Country</label>
                      <select name="country" value={formData.country} onChange={handleFormChange}>
                        <option value="">Select Country</option>
                        <option value="UAE">UAE</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Kuwait">Kuwait</option>
                        <option value="Bahrain">Bahrain</option>
                        <option value="Oman">Oman</option>
                      </select>
                    </div>
                    <div className="sp-form-group">
                      <label>City</label>
                      <input type="text" name="city" value={formData.city} onChange={handleFormChange} placeholder="Enter city" />
                    </div>
                  </div>

                  <div className="sp-form-row">
                    <div className="sp-form-group">
                      <label>Start Date</label>
                      <input type="date" name="startDate" value={formData.startDate} onChange={handleFormChange} />
                    </div>
                    <div className="sp-form-group">
                      <label>End Date</label>
                      <input type="date" name="endDate" value={formData.endDate} onChange={handleFormChange} />
                    </div>
                  </div>

                  <div className="sp-form-row">
                    <div className="sp-form-group sp-form-full">
                      <label>Address</label>
                      <input type="text" name="address" value={formData.address} onChange={handleFormChange} placeholder="Enter project address" />
                    </div>
                  </div>

                  <div className="sp-form-divider"></div>
                  <h3 className="sp-form-section-title">Contact Information</h3>

                  <div className="sp-form-row">
                    <div className="sp-form-group">
                      <label>Company Name</label>
                      <input type="text" name="companyName" value={formData.companyName} onChange={handleFormChange} placeholder="Company name" />
                    </div>
                    <div className="sp-form-group">
                      <label>Contact Name</label>
                      <input type="text" name="contactName" value={formData.contactName} onChange={handleFormChange} placeholder="Contact person" />
                    </div>
                  </div>

                  <div className="sp-form-row">
                    <div className="sp-form-group">
                      <label>Contact Email</label>
                      <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleFormChange} placeholder="email@example.com" />
                    </div>
                    <div className="sp-form-group">
                      <label>Contact Phone</label>
                      <input type="text" name="contactPhone" value={formData.contactPhone} onChange={handleFormChange} placeholder="+971 XX XXX XXXX" />
                    </div>
                  </div>

                  <div className="sp-form-row">
                    <div className="sp-form-group">
                      <label>Status</label>
                      <select name="status" value={formData.status} onChange={handleFormChange}>
                        <option value="draft">Draft</option>
                        <option value="pending_approval">Pending Approval</option>
                        <option value="published">Published</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div className="sp-form-group">
                      <label>Currency</label>
                      <select name="currency" value={formData.currency} onChange={handleFormChange}>
                        {currencies.map(c => (
                          <option key={c.code} value={c.code}>{c.code}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== STEP 2: Description ===== */}
              {wizardStep === 2 && (
                <div className="sp-wizard-content">
                  <div className="sp-form-group sp-form-full">
                    <label>Project Description <span className="sp-required">*</span></label>
                    <p className="sp-form-hint">Write a detailed description of your project including scope, requirements, and any other relevant information.</p>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      rows="12"
                      placeholder="Enter detailed project description...\n\nInclude information such as:\n• Project scope and objectives\n• Key requirements and specifications\n• Timeline expectations\n• Material preferences\n• Any special considerations"
                      className="sp-description-textarea"
                    />
                  </div>
                </div>
              )}

              {/* ===== STEP 3: Images ===== */}
              {wizardStep === 3 && (
                <div className="sp-wizard-content">
                  {/* Main Image Preview */}
                  <div className="sp-image-preview-area">
                    {formData.image ? (
                      <img src={getImageUrl(formData.image)} alt="Project" className="sp-image-preview-img" />
                    ) : (
                      <div className="sp-image-preview-empty">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#ccc" strokeWidth="1.5"/>
                          <circle cx="8.5" cy="8.5" r="1.5" stroke="#ccc" strokeWidth="1.5"/>
                          <path d="M21 15L16 10L5 21" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p>No image selected</p>
                        <span>Select an image from the gallery below or enter a URL</span>
                      </div>
                    )}
                  </div>

                  {/* Image URL Input */}
                  <div className="sp-form-group sp-form-full" style={{ marginBottom: '16px' }}>
                    <label>Image URL</label>
                    <input
                      type="text"
                      name="image"
                      value={formData.image}
                      onChange={handleFormChange}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  {/* Stock Image Gallery */}
                  <div className="sp-image-gallery">
                    <label className="sp-gallery-label">Or select from gallery:</label>
                    <div className="sp-gallery-grid">
                      {stockImages.map((url, idx) => (
                        <div
                          key={idx}
                          className={`sp-gallery-item ${formData.image === url ? 'selected' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, image: url }))}
                        >
                          <img src={url} alt={`Construction ${idx + 1}`} />
                          {formData.image === url && (
                            <div className="sp-gallery-check">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M20 6L9 17L4 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Wizard Footer */}
              <div className="sp-modal-footer sp-wizard-footer">
                {wizardStep > 1 ? (
                  <button type="button" className="sp-btn-cancel" onClick={() => setWizardStep(wizardStep - 1)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                      <path d="M19 12H5" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 19L5 12L12 5" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Back
                  </button>
                ) : (
                  <button type="button" className="sp-btn-cancel" onClick={closeCreateModal}>Cancel</button>
                )}

                {wizardStep < 3 ? (
                  <button
                    type="button"
                    className="sp-btn-save"
                    onClick={() => {
                      if (wizardStep === 1 && !formData.title.trim()) {
                        setFormError('Project title is required.');
                        return;
                      }
                      if (wizardStep === 2 && !formData.description.trim()) {
                        setFormError('Description is required.');
                        return;
                      }
                      setFormError('');
                      setWizardStep(wizardStep + 1);
                    }}
                  >
                    Next Step
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 6, verticalAlign: 'middle' }}>
                      <path d="M5 12H19" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 5L19 12L12 19" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="sp-btn-save"
                    disabled={formSaving}
                    onClick={handleFormSubmit}
                  >
                    {formSaving ? 'Saving...' : editingProject ? 'Update Project' : 'Create Project'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== DETAILS MODAL ==================== */}
      {showDetailsModal && (
        <div className="sp-modal-overlay" onClick={closeDetailsModal}>
          <div className="sp-modal sp-modal-details" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <h2>Project Details</h2>
              <button className="sp-modal-close" onClick={closeDetailsModal}>&times;</button>
            </div>
            <div className="sp-modal-body">
              {detailsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div className="sp-loading-spinner"></div>
                  <p style={{ color: '#888', marginTop: '8px' }}>Loading details...</p>
                </div>
              ) : detailsProject ? (
                <div className="sp-details-content">
                  {/* Image */}
                  {detailsProject.image && (
                    <div className="sp-details-image-wrapper">
                      <img src={getImageUrl(detailsProject.image)} alt={detailsProject.title} className="sp-details-image" />
                    </div>
                  )}

                  {/* Additional Images Gallery */}
                  {detailsProject.images && (() => {
                    try {
                      const imgs = typeof detailsProject.images === 'string' ? JSON.parse(detailsProject.images) : detailsProject.images;
                      if (Array.isArray(imgs) && imgs.length > 0) {
                        return (
                          <div className="sp-details-gallery">
                            <h4 style={{ margin: '16px 0 8px', fontSize: '14px', color: '#333' }}>Project Images ({imgs.length})</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {imgs.map((imgUrl, idx) => (
                                <img key={idx} src={getImageUrl(imgUrl)} alt={`Project ${idx + 1}`} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #eee' }} />
                              ))}
                            </div>
                          </div>
                        );
                      }
                    } catch (e) {}
                    return null;
                  })()}

                  <h3 className="sp-details-title">{detailsProject.title}</h3>

                  <div className="sp-details-badges">
                    <span className={`sp-status-badge ${(detailsProject.status || 'draft')}`}>
                      {({
                        draft: 'Draft',
                        pending_approval: 'Pending',
                        approved: 'Approved',
                        published: 'Published',
                        unpublished: 'Unpublished',
                        rejected: 'Rejected',
                        archived: 'Archived',
                        completed: 'Completed'
                      })[detailsProject.status] || detailsProject.status || 'Draft'}
                    </span>
                    {detailsProject.projectType && (
                      <span className="sp-details-category-badge">{detailsProject.projectType}</span>
                    )}
                  </div>

                  <div className="sp-details-description">
                    <h4>Description</h4>
                    <p>{detailsProject.description}</p>
                  </div>

                  <div className="sp-details-grid">
                    <div className="sp-details-item">
                      <span className="sp-details-label">Country</span>
                      <span className="sp-details-value">{detailsProject.country || '-'}</span>
                    </div>
                    <div className="sp-details-item">
                      <span className="sp-details-label">City</span>
                      <span className="sp-details-value">{detailsProject.city || '-'}</span>
                    </div>
                    <div className="sp-details-item">
                      <span className="sp-details-label">Address</span>
                      <span className="sp-details-value">{detailsProject.address || '-'}</span>
                    </div>
                    <div className="sp-details-item">
                      <span className="sp-details-label">Budget</span>
                      <span className="sp-details-value">{detailsProject.budget ? `${detailsProject.currency || selectedCurrency.code} ${Number(detailsProject.budget).toLocaleString()}` : '-'}</span>
                    </div>
                    <div className="sp-details-item">
                      <span className="sp-details-label">Start Date</span>
                      <span className="sp-details-value">{formatDateLong(detailsProject.startDate)}</span>
                    </div>
                    <div className="sp-details-item">
                      <span className="sp-details-label">End Date</span>
                      <span className="sp-details-value">{formatDateLong(detailsProject.endDate)}</span>
                    </div>
                    <div className="sp-details-item">
                      <span className="sp-details-label">Views</span>
                      <span className="sp-details-value">{detailsProject.views || 0}</span>
                    </div>
                    <div className="sp-details-item">
                      <span className="sp-details-label">Created</span>
                      <span className="sp-details-value">{formatDateLong(detailsProject.createdAt)}</span>
                    </div>
                  </div>

                  {(detailsProject.companyName || detailsProject.contactName || detailsProject.contactEmail || detailsProject.contactPhone) && (
                    <>
                      <div className="sp-form-divider"></div>
                      <h4 className="sp-details-section-heading">Contact Information</h4>
                      <div className="sp-details-grid">
                        <div className="sp-details-item">
                          <span className="sp-details-label">Company</span>
                          <span className="sp-details-value">{detailsProject.companyName || '-'}</span>
                        </div>
                        <div className="sp-details-item">
                          <span className="sp-details-label">Contact Name</span>
                          <span className="sp-details-value">{detailsProject.contactName || '-'}</span>
                        </div>
                        <div className="sp-details-item">
                          <span className="sp-details-label">Email</span>
                          <span className="sp-details-value">{detailsProject.contactEmail || '-'}</span>
                        </div>
                        <div className="sp-details-item">
                          <span className="sp-details-label">Phone</span>
                          <span className="sp-details-value">{detailsProject.contactPhone || '-'}</span>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="sp-modal-footer">
                    <button type="button" className="sp-btn-cancel" onClick={closeDetailsModal}>Close</button>
                    <button type="button" className="sp-btn-save" onClick={() => { closeDetailsModal(); navigate('/specialist-projects/create', { state: { project: detailsProject } }); }}>Edit Project</button>
                  </div>
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>Project not found.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </SpecialistDashboardLayout>
  );
};

export default SpecialistProjectsPage;
